import {useState} from 'react'
import {View} from 'react-native'
import {type MacroMessageDescriptor} from '@lingui/core/macro'
import {useLingui} from '@lingui/react/macro'
import {useMutation, useQueryClient} from '@tanstack/react-query'

import {CAPSULE_GRADES, gradeLabel, KINDERGARTEN_GRADES} from '#/lib/curriculum'
import {useClassroomActor} from '#/state/classrooms'
import {
  classroomKeyPrefixes,
  invalidateClassroom,
} from '#/state/queries/classrooms'
import {RQKEY as PROFILE_RQKEY, useProfileQuery} from '#/state/queries/profile'
import {useAgent} from '#/state/session'
import {
  LEARNING_STAGE_ICONS,
  LEARNING_STAGES,
} from '#/screens/Onboarding/StepGoal/data'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {type DialogControlProps} from '#/components/Dialog'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'

/**
 * A learner's own level, pickable.
 *
 * Onboarding writes the level once — the stage, and the year inside it — and
 * nothing else in the app ever asked again. That was enough while the level only
 * shaped what was suggested, and it is not enough now that it is a **door**: a
 * classroom pinned to a year takes the learners of that year, so a learner who
 * has moved up, or who answered wrong the first time, is standing outside a room
 * that a two-tap answer would let them into. This is that answer, in the place
 * the door is.
 *
 * It speaks the classroom's own vocabulary on purpose. The years offered are the
 * app's shared ones — the same codes a room's year and a capsule's year carry
 * (`#/lib/curriculum`) — because that is what the door reads: a stage and a year
 * that mean the same thing on both sides of the question. It is saved on the
 * account with `putLearnerLevel`, which changes the level and nothing else of
 * the onboarding answers, and is what the server reads when it decides whether
 * a classroom takes the learner. Only a learner has a level.
 */

/**
 * The years a stage has, in the vocabulary classrooms are pinned to.
 *
 * A stage that maps to no list — University, the two self-taught stages — has no
 * year at all, which is a real answer and the one those stages have always given
 * the door (`learnerMayJoinClassroom` compares a year only against a room that
 * names one).
 */
const YEARS_FOR_STAGE: Record<
  string,
  {label: MacroMessageDescriptor; value: string}[]
> = {
  'Preschool / Kindergarten': KINDERGARTEN_GRADES,
  'Primary school': CAPSULE_GRADES.primary,
  'Middle School': CAPSULE_GRADES.college,
  'High School': CAPSULE_GRADES.secondary,
}

/**
 * The name a year is stored under: the label a person reads, not its code.
 *
 * The label carries the app's own keycap (`2️⃣ 2nd year`) because that is what
 * gets printed; the answer is kept without it, which is the same name onboarding
 * writes down (`2nd year`) and the one a person would say out loud. The two are
 * the same year to every comparison in the app (`levelAgreesWithGrade` reads
 * through the keycap), and the plainer one is the one to keep.
 */
function gradeName(code: string): string {
  const label = gradeLabel(code)?.message ?? code
  return label.replace(/^\s*\d\ufe0f?\u20e3\s*/, '')
}

/** The letters and digits of a level, with emoji, keycaps and spacing thrown away. */
function plain(value: string): string {
  return value
    .toLowerCase()
    .replace(/\d\ufe0f?\u20e3/g, '')
    .replace(/[^a-z0-9]/g, '')
}

/** Which of a stage's years an already-chosen answer names, if it names one. */
function codeForYear(stage: string, chosen: string | null): string | null {
  if (!chosen) return null
  const years = YEARS_FOR_STAGE[stage] ?? []
  const found = years.find(
    year =>
      year.value === chosen || plain(gradeName(year.value)) === plain(chosen),
  )
  return found?.value ?? null
}

/** The learner's level, as a line: "Primary school · 1st year". */
export function learnerLevelText(level: {
  learningStage?: string | null
  gradeLevel?: string | null
}): string {
  return [level.learningStage, level.gradeLevel].filter(Boolean).join(' · ')
}

type LearnerLevel = {learningStage: string; gradeLevel: string | null}

export function LearnerLevelDialog({
  control,
  onSaved,
  classroomId,
}: {
  control: DialogControlProps
  /** Told after the answer is saved, so whatever asked can read it again. */
  onSaved?: () => void
  /** The room that asked, refreshed after the save. */
  classroomId?: string
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const actor = useClassroomActor()
  const agent = useAgent()
  const queryClient = useQueryClient()
  // The level is read off the profile, so nothing is saved before it is here
  const {data: profile} = useProfileQuery({did: actor?.did})

  const [stage, setStage] = useState<string | null>(
    () => actor?.learningStage ?? null,
  )
  const [year, setYear] = useState<string | null>(() =>
    codeForYear(actor?.learningStage ?? '', actor?.gradeLevel ?? null),
  )

  const save = useMutation({
    mutationFn: async (level: LearnerLevel) => {
      await agent.call('app.taderok.actor.putLearnerLevel', undefined, {
        learningStage: level.learningStage,
        ...(level.gradeLevel ? {gradeLevel: level.gradeLevel} : {}),
      })
    },
    onSuccess: async () => {
      if (!actor) return
      await Promise.all([
        queryClient.invalidateQueries({queryKey: PROFILE_RQKEY(actor.did)}),
        classroomId
          ? invalidateClassroom(queryClient, classroomId)
          : Promise.all([
              queryClient.invalidateQueries({
                queryKey: classroomKeyPrefixes.room(),
              }),
              queryClient.invalidateQueries({
                queryKey: classroomKeyPrefixes.byRoute(),
              }),
            ]),
      ])
    },
  })

  const years = stage ? (YEARS_FOR_STAGE[stage] ?? []) : []
  const canSave =
    !!actor &&
    !!profile &&
    !!stage &&
    (years.length === 0 || !!year) &&
    !save.isPending

  const onStageChange = (next: string) => {
    /* A year belongs to one stage's list, so moving stage clears it. */
    setStage(next)
    setYear(null)
  }

  const onClose = () => {
    setStage(actor?.learningStage ?? null)
    setYear(codeForYear(actor?.learningStage ?? '', actor?.gradeLevel ?? null))
    save.reset()
  }

  const onSave = () => {
    if (!canSave || !stage) return
    const level: LearnerLevel = {
      learningStage: stage,
      gradeLevel: year ? gradeName(year) : null,
    }
    save.mutate(level, {
      onSuccess: () => {
        control.close(() => {
          Toast.show(l`Your level is now ${learnerLevelText(level)}`)
          onSaved?.()
        })
      },
    })
  }

  return (
    <Dialog.Outer control={control} onClose={onClose}>
      <Dialog.Handle />
      <Dialog.ScrollableInner label={l`Change your level`}>
        <View style={[a.gap_lg]}>
          <Text style={[a.text_xl, a.font_bold]}>{l`Your level`}</Text>
          <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
            {l`Your level is the stage you are in and the year inside it. It is what a classroom checks before it lets you in, and you can change it whenever it moves.`}
          </Text>

          <View style={[a.gap_sm]}>
            <Text style={[a.text_md, a.font_semi_bold]}>{l`Stage`}</Text>
            <View style={[a.flex_row, a.flex_wrap, a.gap_sm]}>
              {LEARNING_STAGES.map(option => (
                <Button
                  key={option}
                  testID={`learnerLevelStage-${option}`}
                  label={option}
                  size="small"
                  color={stage === option ? 'primary' : 'secondary'}
                  onPress={() => onStageChange(option)}>
                  <ButtonText emoji>
                    {LEARNING_STAGE_ICONS[option]} {option}
                  </ButtonText>
                </Button>
              ))}
            </View>
          </View>

          {years.length > 0 ? (
            <View style={[a.gap_sm]}>
              <Text style={[a.text_md, a.font_semi_bold]}>{l`Year`}</Text>
              <View style={[a.flex_row, a.flex_wrap, a.gap_sm]}>
                {years.map(option => (
                  <Button
                    key={option.value}
                    testID={`learnerLevelYear-${option.value}`}
                    label={l(option.label)}
                    size="small"
                    color={year === option.value ? 'primary' : 'secondary'}
                    onPress={() => setYear(option.value)}>
                    <ButtonText emoji>{l(option.label)}</ButtonText>
                  </Button>
                ))}
              </View>
            </View>
          ) : null}

          {stage && years.length === 0 ? (
            <Text
              testID="learnerLevelNoYear"
              style={[a.text_sm, t.atoms.text_contrast_medium]}>
              {l`This stage has no year — every classroom at this stage takes you.`}
            </Text>
          ) : null}

          {save.isError ? (
            <Text
              accessibilityRole="alert"
              testID="learnerLevelError"
              style={[a.text_md, {color: t.palette.negative_500}]}>
              {l`Your level could not be saved. Check your connection and try again.`}
            </Text>
          ) : null}

          <Button
            testID="learnerLevelSave"
            label={l`Save my level`}
            size="large"
            color="primary"
            disabled={!canSave}
            onPress={onSave}>
            <ButtonText>{l`Save my level`}</ButtonText>
          </Button>
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}

/**
 * The offer to change your level, as a line of text.
 *
 * It sits inside a sentence — the refusal that says *your level is different*,
 * the announcement that says *this room moved* — so it is a piece of text that
 * does something rather than a button beside one, and the sentence it lives in
 * reads the same whether it is tapped or not.
 */
export function LearnerLevelLink({
  children,
  testID,
  onSaved,
  classroomId,
}: {
  children?: React.ReactNode
  testID?: string
  onSaved?: () => void
  classroomId?: string
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const control = Dialog.useDialogControl()
  const actor = useClassroomActor()

  // Only a learner has a level to change
  if (actor?.accountType !== 'learner') return null

  return (
    <>
      <Text
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={l`Change your level`}
        accessibilityHint={l`Opens the picker for your stage and year`}
        onPress={() => control.open()}
        style={[
          a.font_semi_bold,
          {
            color: t.palette.primary_500,
            textDecorationLine: 'underline',
          },
        ]}>
        {children ?? l`change your level in your settings`}
      </Text>
      <LearnerLevelDialog
        control={control}
        onSaved={onSaved}
        classroomId={classroomId}
      />
    </>
  )
}

/** The same offer as a button, for a card that has room for one. */
export function LearnerLevelButton({
  testID,
  onSaved,
  label,
  color = 'primary',
  classroomId,
}: {
  testID?: string
  onSaved?: () => void
  label?: string
  color?: 'primary' | 'secondary'
  classroomId?: string
}) {
  const {t: l} = useLingui()
  const control = Dialog.useDialogControl()
  const actor = useClassroomActor()

  // Only a learner has a level to change
  if (actor?.accountType !== 'learner') return null

  return (
    <>
      <Button
        testID={testID}
        label={label ?? l`Change my level`}
        color={color}
        size="small"
        onPress={() => control.open()}>
        <ButtonText>{label ?? l`Change my level`}</ButtonText>
      </Button>
      <LearnerLevelDialog
        control={control}
        onSaved={onSaved}
        classroomId={classroomId}
      />
    </>
  )
}
